// src/pages/MyTasks.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  useGetMyTasksQuery,
  useRespondToTaskMutation,
  useStartTaskMutation,
  useCompleteTaskMutation,
  useRegisterDeviceFromTaskMutation,
} from "../../redux/admin/taskApi";

// ✅ MUI
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Grid,
  Alert,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// ✅ MUI DataGrid (standard professional table)
import { DataGrid } from "@mui/x-data-grid";

// lucide icons
import {
  RefreshCw,
  X,
  Navigation2,
  Phone,
  Check,
  Ban,
  Play,
  Flag,
  Cpu,
  Camera,
  ExternalLink,
} from "lucide-react";
import api from "../../redux/apis/api";

/**
 * ✅ FULL MUI TABLE VIEW (DataGrid)
 * - Easy to understand
 * - Full details columns
 * - Actions (Accept/Reject/Start/Complete/Register) same as your logic
 * - Dialogs: Complete / Reject / Register Device
 */

const MyTasks = () => {
  const FALLBACK_PROTOCOL_OPTIONS = [];
  const FALLBACK_MODELS_BY_PROTOCOL = {};

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const [filterStatus, setFilterStatus] = useState("");
  const [selectedTask, setSelectedTask] = useState(null);

  // dialogs
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  // API
// API
const { data, isLoading, refetch, error } = useGetMyTasksQuery(
  filterStatus ? { status: filterStatus } : undefined
);

// ✅ super-safe normalize
const tasks = useMemo(() => {
  const d = data;

  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.tasks)) return d.tasks;

  // common patterns
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.data?.tasks)) return d.data.tasks;
  if (Array.isArray(d?.data?.data)) return d.data.data;

  if (Array.isArray(d?.result)) return d.result;
  if (Array.isArray(d?.items)) return d.items;

  return [];
}, [data]);

const [respondToTask, { isLoading: responding }] = useRespondToTaskMutation();
  const [startTask, { isLoading: starting }] = useStartTaskMutation();
  const [completeTask, { isLoading: completing }] = useCompleteTaskMutation();
  const [registerDeviceFromTask, { isLoading: registering }] = useRegisterDeviceFromTaskMutation();

  // complete form
  const [completeData, setCompleteData] = useState({
    fieldNotes: "",
    photos: [],
    clientSignature: "",
  });

  // reject
  const [rejectReason, setRejectReason] = useState("");

  // register form
  const [registerData, setRegisterData] = useState({
    imei: "",
    serialNumber: "",
    model: "",
    deviceType: "concox",
    purchaseDate: "",

    vehicleNumber: "",
    vehicleType: "Car",

    msdn: "",
    imsi: "",
    simProvider: "",
    simExpiryDate: "",

    notes: "",
  });
  const [protocolOptions, setProtocolOptions] = useState([]);
  const [modelOptionsByProtocol, setModelOptionsByProtocol] = useState({});
  const [optionsLoading, setOptionsLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const loadOptions = async () => {
      try {
        setOptionsLoading(true);
        const { data } = await api.get("/devices/models/options");
        if (!mounted) return;
        const options = data?.options || {};
        const protocols = Array.isArray(options.protocols) ? options.protocols : [];
        const modelMap =
          options.modelsByProtocol && typeof options.modelsByProtocol === "object"
            ? options.modelsByProtocol
            : {};
        setProtocolOptions(protocols);
        setModelOptionsByProtocol(modelMap);
      } catch (_) {
        if (!mounted) return;
        setProtocolOptions(FALLBACK_PROTOCOL_OPTIONS);
        setModelOptionsByProtocol(FALLBACK_MODELS_BY_PROTOCOL);
      } finally {
        if (mounted) setOptionsLoading(false);
      }
    };
    loadOptions();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!showRegisterModal) return;
    if (!protocolOptions.length) return;
    const currentType = String(registerData.deviceType || "").trim().toLowerCase();
    const hasType = protocolOptions.some((p) => String(p.key || "").toLowerCase() === currentType);
    const nextType = hasType ? currentType : String(protocolOptions[0]?.key || "concox").toLowerCase();
    const models = modelOptionsByProtocol[nextType] || [];
    if (nextType !== registerData.deviceType || (models.length && !models.includes(registerData.model))) {
      setRegisterData((p) => ({
        ...p,
        deviceType: nextType,
        model: models.includes(p.model) ? p.model : (models[0] || p.model || ""),
      }));
    }
  }, [showRegisterModal, protocolOptions, modelOptionsByProtocol, registerData.deviceType, registerData.model]);

  // helpers
  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
      : "-";

  const formatDateTime = (date) => (date ? new Date(date).toLocaleString("en-IN") : "-");

  const navigateLink = (task) => {
    if (task?.siteLatitude && task?.siteLongitude) {
      return `https://www.google.com/maps/dir/?api=1&destination=${task.siteLatitude},${task.siteLongitude}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(task?.siteAddress || "")}`;
  };

  const statusChip = (status) => {
    const map = {
      pending: { label: "PENDING", color: "warning" },
      accepted: { label: "ACCEPTED", color: "info" },
      in_progress: { label: "IN PROGRESS", color: "primary" },
      completed: { label: "COMPLETED", color: "success" },
      rejected: { label: "REJECTED", color: "error" },
    };
    const s = map[status] || { label: String(status || "—").toUpperCase(), color: "default" };
    return <Chip size="small" label={s.label} color={s.color} sx={{ fontWeight: 900, borderRadius: 2 }} />;
  };

  const priorityChip = (priority) => {
    const map = {
      low: { label: "LOW", color: "default" },
      medium: { label: "MEDIUM", color: "info" },
      high: { label: "HIGH", color: "warning" },
      urgent: { label: "URGENT", color: "error" },
    };
    const p = map[priority] || map.medium;
    return <Chip size="small" label={p.label} color={p.color} sx={{ fontWeight: 900, borderRadius: 2 }} />;
  };

  // actions
const handleAccept = async (taskId) => {
  try {
    await respondToTask({ taskId, accept: true }).unwrap();
    alert("✅ Task accepted successfully!");
    refetch();
  } catch (err) {
    alert(err?.data?.msg || "Failed to accept task");
  }
};


  const openRejectModal = (task) => {
    setSelectedTask(task);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!selectedTask || !rejectReason.trim()) return;
    try {
      await respondToTask({
        taskId: selectedTask._id,
        accept: false,
        rejectionReason: rejectReason,
      }).unwrap();
      alert("Task rejected");
      setShowRejectModal(false);
      setSelectedTask(null);
      setRejectReason("");
      refetch();
    } catch (err) {
      alert(err?.data?.msg || "Failed to reject task");
    }
  };

  const handleStart = async (taskId) => {
    try {
      const result = await startTask(taskId).unwrap();
      alert("Task started successfully!");
      if (result?.warning) {
        console.warn("Inventory issue while starting task:", result.warning);
      }
      refetch();
    } catch (err) {
      alert(err?.data?.msg || "Failed to start task");
    }
  };

  const openCompleteModal = (task) => {
    setSelectedTask(task);
    setCompleteData({ fieldNotes: "", photos: [], clientSignature: "" });
    setShowCompleteModal(true);
  };

  const handleComplete = async () => {
    if (!selectedTask || !completeData.fieldNotes.trim()) return;
    try {
      const result = await completeTask({
        taskId: selectedTask._id,
        ...completeData,
      }).unwrap();
      alert("Task completed successfully!");
      if (result?.warning) {
        console.warn("Inventory handover issue while completing task:", result.warning);
      }
      setShowCompleteModal(false);
      setSelectedTask(null);
      setCompleteData({ fieldNotes: "", photos: [], clientSignature: "" });
      refetch();
    } catch (err) {
      alert(err?.data?.msg || "Failed to complete task");
    }
  };

  const openRegisterModal = (task) => {
    setSelectedTask(task);
    setShowRegisterModal(true);

    // ✅ prefill (easy for field employee)
    setRegisterData((p) => ({
      ...p,
      imei: task.deviceImei || p.imei,
      vehicleNumber: task.vehicleNumber || p.vehicleNumber,
      model: p.model || "",
      deviceType: p.deviceType || (protocolOptions[0]?.key || "concox"),
    }));
  };

  const handleRegisterDevice = async () => {
    if (!selectedTask) return;

    if (!registerData.imei || !registerData.model || !registerData.vehicleNumber) {
      alert("IMEI, Model and Vehicle Number are required");
      return;
    }

    try {
      await registerDeviceFromTask({
        taskId: selectedTask._id,
        name: registerData.model,

        // DEVICE
        imei: registerData.imei,
        serialNumber: registerData.serialNumber,
        model: registerData.model,
        deviceType: registerData.deviceType,
        purchaseDate: registerData.purchaseDate,

        // VEHICLE
        vehicleNumber: registerData.vehicleNumber,
        vehicleType: registerData.vehicleType,

        // SIM
        msdn: registerData.msdn,
        imsi: registerData.imsi,
        simProvider: registerData.simProvider,
        simExpiryDate: registerData.simExpiryDate,

        // EXTRA
        notes: registerData.notes,
      }).unwrap();

      alert("✅ Device registered successfully!");
      setShowRegisterModal(false);
      setRegisterData({
        imei: "",
        serialNumber: "",
        model: "",
        deviceType: protocolOptions[0]?.key || "concox",
        purchaseDate: "",
        vehicleNumber: "",
        vehicleType: "Car",
        msdn: "",
        imsi: "",
        simProvider: "",
        simExpiryDate: "",
        notes: "",
      });
      refetch();
    } catch (err) {
      alert(err?.data?.msg || "Device registration failed");
    }
  };

  // photos
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const newPhotos = files.map((file) => ({
      url: URL.createObjectURL(file),
      type: "after",
      _file: file,
    }));
    setCompleteData((prev) => ({
      ...prev,
      photos: [...prev.photos, ...newPhotos],
    }));
  };

  // stats
  const stats = useMemo(() => {
    const pending = tasks.filter((t) => t.status === "pending").length;
    const accepted = tasks.filter((t) => t.status === "accepted").length;
    const progress = tasks.filter((t) => t.status === "in_progress").length;
    const completed = tasks.filter((t) => t.status === "completed").length;
    return { pending, accepted, progress, completed };
  }, [tasks]);

  // ✅ DataGrid rows
  const rows = useMemo(() => {
    return (tasks || []).map((t) => ({
      id: t._id, // DataGrid needs id
      ...t,
    }));
  }, [tasks]);

  // ✅ Columns (Full details, easy readable)
  const columns = useMemo(
    () => [
      {
        field: "status",
        headerName: "Status",
        width: 140,
        sortable: true,
        renderCell: (params) => statusChip(params.value),
      },
      {
        field: "priority",
        headerName: "Priority",
        width: 140,
        sortable: true,
        renderCell: (params) => priorityChip(params.value),
      },
      {
        field: "title",
        headerName: "Title",
        minWidth: 220,
        flex: 1,
        sortable: true,
        renderCell: (params) => (
          <Stack spacing={0.4} sx={{ py: 0.8 }}>
            <Typography sx={{ fontWeight: 900, lineHeight: 1.2 }}>{params.value || "-"}</Typography>
            <Typography variant="caption" color="text.secondary">
              Type: {String(params.row.taskType || "-").replaceAll("_", " ")}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "scheduledDate",
        headerName: "Schedule",
        width: 170,
        sortable: true,
        valueGetter: (params) => params.value,
        renderCell: (params) => (
          <Stack spacing={0.2} sx={{ py: 0.8 }}>
            <Typography sx={{ fontWeight: 800 }}>{formatDate(params.row.scheduledDate)}</Typography>
            {params.row.scheduledTime ? (
              <Typography variant="caption" color="text.secondary">
                Time: {params.row.scheduledTime}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Time: -
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: "siteAddress",
        headerName: "Site Location",
        minWidth: 260,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Stack spacing={0.6} sx={{ py: 0.8, width: "100%" }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
              title={params.value || ""}
            >
              {params.value || "-"}
            </Typography>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                component="a"
                href={navigateLink(params.row)}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<Navigation2 size={16} />}
                sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
              >
                Navigate
              </Button>
              <Tooltip title="Open in Maps">
                <IconButton
                  size="small"
                  component="a"
                  href={navigateLink(params.row)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink size={16} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        ),
      },
      {
        field: "clientName",
        headerName: "Client",
        width: 210,
        sortable: false,
        renderCell: (params) => {
          const name = params.row.clientName || "-";
          const phone = params.row.clientPhone || "";
          return (
            <Stack spacing={0.4} sx={{ py: 0.8 }}>
              <Typography sx={{ fontWeight: 800 }}>{name}</Typography>
              {phone ? (
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {phone}
                  </Typography>
                  <Tooltip title="Call Client">
                    <IconButton size="small" component="a" href={`tel:${phone}`}>
                      <Phone size={16} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Phone: -
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: "vehicleNumber",
        headerName: "Vehicle / IMEI",
        width: 220,
        sortable: false,
        renderCell: (params) => (
          <Stack spacing={0.4} sx={{ py: 0.8 }}>
            <Typography sx={{ fontWeight: 900 }}>{params.row.vehicleNumber || "-"}</Typography>
            <Typography variant="caption" color="text.secondary">
              IMEI: {params.row.deviceImei || "-"}
            </Typography>
          </Stack>
        ),
      },
      // ✅ ADD THIS COLUMN IN MyTasks.jsx columns[]

{
  field: "inventory",
  headerName: "Inventory",
  width: 240,
  sortable: false,
  renderCell: (params) => {
    const t = params.row;

    if (!t?.inventoryRequired) {
      return <Typography variant="body2" color="text.secondary">—</Typography>;
    }

    const status = t.inventoryStatus || "pending";

    const color =
      status === "handed_over"
        ? "success"
        : status === "issued"
        ? "info"
        : status === "returned"
        ? "warning"
        : status === "failed"
        ? "error"
        : "default";

    const itemName = t.inventoryIssue?.itemName || t.inventoryIssue?.item?.name || ""; // optional if backend sends
    const serial = t.inventoryIssue?.serial;
    const qty = t.inventoryIssue?.qty;

    return (
      <Stack spacing={0.4} sx={{ py: 0.8 }}>
        <Chip
          size="small"
          label={`Required • ${status}`}
          color={color}
          sx={{ fontWeight: 900, borderRadius: 2, textTransform: "capitalize" }}
        />

        {itemName ? (
          <Typography variant="caption" color="text.secondary">
            Item: {itemName}
          </Typography>
        ) : null}

        {serial ? (
          <Typography variant="caption" color="text.secondary">
            Serial: {serial}
          </Typography>
        ) : qty ? (
          <Typography variant="caption" color="text.secondary">
            Qty: {qty}
          </Typography>
        ) : null}

        {t.inventoryHandover?.ownerLabel ? (
          <Typography variant="caption" color="text.secondary">
            To: {t.inventoryHandover.ownerLabel}
          </Typography>
        ) : null}
      </Stack>
    );
  },
},

      {
        field: "assignedBy",
        headerName: "Assigned By",
        width: 210,
        sortable: false,
        renderCell: (params) => {
          const by = params.row.assignedBy?.username || "-";
          const phone = params.row.assignedBy?.mobileNo || "";
          return (
            <Stack spacing={0.4} sx={{ py: 0.8 }}>
              <Typography sx={{ fontWeight: 800 }}>{by}</Typography>
              {phone ? (
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {phone}
                  </Typography>
                  <Tooltip title="Call Assigned By">
                    <IconButton size="small" component="a" href={`tel:${phone}`}>
                      <Phone size={16} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Phone: -
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: "completionInfo",
        headerName: "Result",
        width: 220,
        sortable: false,
        renderCell: (params) => {
          const { status, completedAt, rejectionReason, verificationStatus } = params.row;

          if (status === "completed") {
            return (
              <Stack spacing={0.4} sx={{ py: 0.8 }}>
                <Chip size="small" color="success" label="Completed" sx={{ fontWeight: 900, borderRadius: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  {completedAt ? formatDateTime(completedAt) : "-"}
                </Typography>
                {verificationStatus ? (
                  <Typography variant="caption" color="text.secondary">
                    Verify: {String(verificationStatus).toUpperCase()}
                  </Typography>
                ) : null}
              </Stack>
            );
          }

          if (status === "rejected") {
            return (
              <Stack spacing={0.4} sx={{ py: 0.8 }}>
                <Chip size="small" color="error" label="Rejected" sx={{ fontWeight: 900, borderRadius: 2 }} />
                <Typography variant="caption" color="text.secondary" title={rejectionReason || ""}>
                  {rejectionReason ? rejectionReason.slice(0, 40) : "-"}
                </Typography>
              </Stack>
            );
          }

          if (verificationStatus) {
            return (
              <Stack spacing={0.4} sx={{ py: 0.8 }}>
                <Chip
                  size="small"
                  color={verificationStatus === "approved" ? "success" : verificationStatus === "rejected" ? "error" : "info"}
                  label={`Verify: ${String(verificationStatus).toUpperCase()}`}
                  sx={{ fontWeight: 900, borderRadius: 2 }}
                />
              </Stack>
            );
          }

          return <Typography variant="body2" color="text.secondary">-</Typography>;
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 320,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const t = params.row;

          // pending -> accept/reject
          if (t.status === "pending") {
            return (
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Check size={16} />}
                  disabled={responding}
                  onClick={() => handleAccept(t._id)}
                  sx={{ borderRadius: 2, fontWeight: 1000, textTransform: "none" }}
                >
                  Accept
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<Ban size={16} />}
                  onClick={() => openRejectModal(t)}
                  sx={{ borderRadius: 2, fontWeight: 1000, textTransform: "none" }}
                >
                  Reject
                </Button>
              </Stack>
            );
          }

          // accepted -> start

if (t.status === "accepted") {
  return (
    <Stack spacing={0.6}>
      <Button
        size="small"
        variant="contained"
        startIcon={<Play size={16} />}
        disabled={starting}
        onClick={() => handleStart(t._id)}
        sx={{ borderRadius: 2, fontWeight: 1000, textTransform: "none" }}
      >
        Start Work
      </Button>

      {t.inventoryRequired && (
        <Typography variant="caption" color="text.secondary">
          Inventory: {String(t.inventoryStatus || "pending").replaceAll("_", " ")}
        </Typography>
      )}
    </Stack>
  );
}


          // in_progress -> complete + register device (optional)
          if (t.status === "in_progress") {
            return (
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<Flag size={16} />}
                  onClick={() => openCompleteModal(t)}
                  sx={{ borderRadius: 2, fontWeight: 1000, textTransform: "none" }}
                >
                  Complete
                </Button>

                {t.allowDeviceRegistration && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Cpu size={16} />}
                    onClick={() => openRegisterModal(t)}
                    sx={{ borderRadius: 2, fontWeight: 1000, textTransform: "none" }}
                  >
                    Register Device
                  </Button>
                )}
              </Stack>
            );
          }

          // completed/rejected -> no actions
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
[
  responding,
  starting,
  completing,
  registering,
  handleAccept,
  handleStart,
  openRejectModal,
  openCompleteModal,
  openRegisterModal,
]
  );

  return (
    <Box sx={{ p: { xs: 1.2, md: 2.5 } }}>
      {/* Header */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.6, md: 2.2 },
          borderRadius: 4,
          borderColor: alpha(theme.palette.divider, 0.9),
          mb: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.2}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 20, md: 26 } }}>
              My Tasks
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Table view — full details, easy actions for field employee
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              select
              size="small"
              label="Filter Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="accepted">Accepted</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </TextField>

            <Button
              variant="outlined"
              onClick={refetch}
              startIcon={<RefreshCw size={18} />}
              sx={{ fontWeight: 900, borderRadius: 2.5, textTransform: "none" }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Stats */}
        <Grid container spacing={1.2}>
          <Grid item xs={6} sm={3}>
            <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 3 }}>
              <Typography variant="caption" color="text.secondary">Pending</Typography>
              <Typography sx={{ fontWeight: 1000, fontSize: 22 }}>{stats.pending}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 3 }}>
              <Typography variant="caption" color="text.secondary">Accepted</Typography>
              <Typography sx={{ fontWeight: 1000, fontSize: 22 }}>{stats.accepted}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 3 }}>
              <Typography variant="caption" color="text.secondary">In Progress</Typography>
              <Typography sx={{ fontWeight: 1000, fontSize: 22 }}>{stats.progress}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 3 }}>
              <Typography variant="caption" color="text.secondary">Completed</Typography>
              <Typography sx={{ fontWeight: 1000, fontSize: 22 }}>{stats.completed}</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 4, overflow: "hidden" }}>
        {isLoading ? (
          <Box sx={{ p: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: isSmDown ? 620 : 680, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id || row._id}
              getRowHeight={() => "auto"} // ✅ readable multi-line cells
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              sx={{
                border: 0,
                "& .MuiDataGrid-columnHeaders": {
                  background: alpha(theme.palette.primary.main, 0.06),
                  fontWeight: 1000,
                },
                "& .MuiDataGrid-cell": {
                  py: 1.2,
                  alignItems: "start",
                },
                "& .MuiDataGrid-row": {
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                },
              }}
              slots={{
                noRowsOverlay: () => (
                  <Box sx={{ p: 5, textAlign: "center" }}>
                    <Typography sx={{ fontWeight: 1000, fontSize: 18 }}>No tasks found</Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.8 }}>
                      Adjust filter or refresh
                    </Typography>
                  </Box>
                ),
              }}
            />
          </Box>
        )}
      </Paper>

      {/* ✅ COMPLETE MODAL */}
      <Dialog
        open={showCompleteModal && Boolean(selectedTask)}
        onClose={() => setShowCompleteModal(false)}
        fullWidth
        maxWidth="md"
        fullScreen={isSmDown}
        PaperProps={{ sx: { borderRadius: isSmDown ? 0 : 4 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography sx={{ fontWeight: 1000 }}>Complete Task</Typography>
            <IconButton onClick={() => setShowCompleteModal(false)}>
              <X size={20} />
            </IconButton>
          </Stack>
        </DialogTitle>

        {selectedTask && (
          <>
            <DialogContent sx={{ pt: 1 }}>
              <Typography sx={{ fontWeight: 1000, fontSize: 18 }}>{selectedTask.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Site: {selectedTask.siteAddress || "-"}
              </Typography>

              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Field Notes *"
                  value={completeData.fieldNotes}
                  onChange={(e) => setCompleteData((p) => ({ ...p, fieldNotes: e.target.value }))}
                  placeholder="Describe the work completed, issues, parts used, etc..."
                  multiline
                  minRows={5}
                  required
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ sm: "center" }} justifyContent="space-between">
                <Typography sx={{ fontWeight: 1000 }}>Upload Photos</Typography>

                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<Camera size={18} />}
                  sx={{ fontWeight: 900, borderRadius: 2.2, textTransform: "none" }}
                >
                  Add Photos
                  <input type="file" accept="image/*" multiple hidden onChange={handlePhotoUpload} />
                </Button>
              </Stack>

              {completeData.photos.length > 0 && (
                <Grid container spacing={1.2} sx={{ mt: 1 }}>
                  {completeData.photos.map((photo, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 3 }}>
                        <Box
                          component="img"
                          src={photo.url}
                          alt={`Upload ${idx + 1}`}
                          sx={{ width: "100%", height: 150, objectFit: "cover", borderRadius: 2 }}
                        />

                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                          <TextField
                            select
                            size="small"
                            label="Type"
                            value={photo.type}
                            onChange={(e) => {
                              const newPhotos = [...completeData.photos];
                              newPhotos[idx].type = e.target.value;
                              setCompleteData((p) => ({ ...p, photos: newPhotos }));
                            }}
                            sx={{ flex: 1 }}
                          >
                            <MenuItem value="before">Before</MenuItem>
                            <MenuItem value="after">After</MenuItem>
                            <MenuItem value="wiring">Wiring</MenuItem>
                            <MenuItem value="device">Device</MenuItem>
                            <MenuItem value="signature">Signature</MenuItem>
                          </TextField>

                          <Button
                            variant="text"
                            color="error"
                            onClick={() => {
                              const newPhotos = completeData.photos.filter((_, i) => i !== idx);
                              setCompleteData((p) => ({ ...p, photos: newPhotos }));
                            }}
                            sx={{ fontWeight: 900, textTransform: "none" }}
                          >
                            Remove
                          </Button>
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}

              {selectedTask.allowDeviceRegistration && (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 3 }}>
                  This task allows device registration. If needed, use <strong>Register Device</strong> action from table.
                </Alert>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ width: "100%" }} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => setShowCompleteModal(false)}
                  sx={{ fontWeight: 900, borderRadius: 2, textTransform: "none" }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  onClick={handleComplete}
                  disabled={completing || !completeData.fieldNotes.trim()}
                  sx={{ fontWeight: 1000, borderRadius: 2, textTransform: "none" }}
                >
                  {completing ? "Submitting..." : "Complete Task"}
                </Button>
              </Stack>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ✅ REJECT MODAL */}
      <Dialog
        open={showRejectModal && Boolean(selectedTask)}
        onClose={() => setShowRejectModal(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isSmDown}
        PaperProps={{ sx: { borderRadius: isSmDown ? 0 : 4 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography sx={{ fontWeight: 1000 }}>Reject Task</Typography>
            <IconButton onClick={() => setShowRejectModal(false)}>
              <X size={20} />
            </IconButton>
          </Stack>
        </DialogTitle>

        {selectedTask && (
          <>
            <DialogContent sx={{ pt: 1 }}>
              <Typography color="text.secondary">
                Please provide a reason for rejecting:
              </Typography>

              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Rejection Reason *"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g., Location too far, equipment not available..."
                  multiline
                  minRows={4}
                  required
                />
              </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ width: "100%" }} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => setShowRejectModal(false)}
                  sx={{ fontWeight: 900, borderRadius: 2, textTransform: "none" }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  onClick={handleReject}
                  disabled={responding || !rejectReason.trim()}
                  startIcon={<Ban size={16} />}
                  sx={{ fontWeight: 1000, borderRadius: 2, textTransform: "none" }}
                >
                  {responding ? "Rejecting..." : "Confirm Reject"}
                </Button>
              </Stack>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ✅ REGISTER DEVICE MODAL */}
      <Dialog
        open={showRegisterModal && Boolean(selectedTask)}
        onClose={() => setShowRegisterModal(false)}
        fullWidth
        maxWidth="md"
        fullScreen={isSmDown}
        PaperProps={{ sx: { borderRadius: isSmDown ? 0 : 4 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography sx={{ fontWeight: 1000 }}>Register Device</Typography>
            <IconButton onClick={() => setShowRegisterModal(false)}>
              <X size={20} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Alert severity="info" sx={{ borderRadius: 3, mb: 2 }}>
            Fill required fields: <strong>IMEI, Model, Vehicle Number</strong>.
          </Alert>

          <Grid container spacing={2}>
            {/* DEVICE */}
            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 1000 }}>Device Details</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="IMEI *"
                value={registerData.imei}
                onChange={(e) => setRegisterData((p) => ({ ...p, imei: e.target.value }))}
                placeholder="15 digit IMEI"
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Serial Number"
                value={registerData.serialNumber}
                onChange={(e) => setRegisterData((p) => ({ ...p, serialNumber: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Protocol"
                value={registerData.deviceType}
                onChange={(e) => {
                  const nextType = e.target.value;
                  const nextModels = modelOptionsByProtocol[nextType] || [];
                  setRegisterData((p) => ({
                    ...p,
                    deviceType: nextType,
                    model: nextModels.includes(p.model) ? p.model : (nextModels[0] || ""),
                  }));
                }}
              >
                {(protocolOptions || []).map((p) => (
                  <MenuItem key={p.key} value={p.key}>
                    {p.label || p.key}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Model *"
                value={registerData.model}
                onChange={(e) => setRegisterData((p) => ({ ...p, model: e.target.value }))}
                required
              >
                {((modelOptionsByProtocol[registerData.deviceType] || [])).map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Purchase Date"
                value={registerData.purchaseDate}
                onChange={(e) => setRegisterData((p) => ({ ...p, purchaseDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* VEHICLE */}
            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 1000 }}>Vehicle Details</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vehicle Number *"
                value={registerData.vehicleNumber}
                onChange={(e) => setRegisterData((p) => ({ ...p, vehicleNumber: e.target.value }))}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Vehicle Type"
                value={registerData.vehicleType}
                onChange={(e) => setRegisterData((p) => ({ ...p, vehicleType: e.target.value }))}
              >
                <MenuItem value="Car">Car</MenuItem>
                <MenuItem value="Bike">Bike</MenuItem>
                <MenuItem value="Truck">Truck</MenuItem>
                <MenuItem value="Bus">Bus</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* SIM */}
            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 1000 }}>SIM Card Details</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SIM Number (MSDN)"
                value={registerData.msdn}
                onChange={(e) => setRegisterData((p) => ({ ...p, msdn: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="IMSI"
                value={registerData.imsi}
                onChange={(e) => setRegisterData((p) => ({ ...p, imsi: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SIM Provider"
                value={registerData.simProvider}
                onChange={(e) => setRegisterData((p) => ({ ...p, simProvider: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="SIM Expiry Date"
                value={registerData.simExpiryDate}
                onChange={(e) => setRegisterData((p) => ({ ...p, simExpiryDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={registerData.notes}
                onChange={(e) => setRegisterData((p) => ({ ...p, notes: e.target.value }))}
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ width: "100%" }} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => setShowRegisterModal(false)}
              sx={{ fontWeight: 900, borderRadius: 2, textTransform: "none" }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleRegisterDevice}
              disabled={registering}
              startIcon={<Cpu size={16} />}
              sx={{ fontWeight: 1000, borderRadius: 2, textTransform: "none" }}
            >
              {registering ? "Registering..." : "Add Device"}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyTasks;
